import { Injectable } from '@angular/core';
import { BaseHttpClient } from '@peca/commons';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { PageableQuery } from '@peca/backoffice/models/pageable-query.model';
import { AutocompleteItem } from '@peca/design-system';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'apps/backoffice/src/environments/environment';
import {
  AnyMarketExtractionDetailsDto,
  AnyMarketExtractionDto,
  AnyMarketExtractionFilter,
  BrandSuggestionDto,
  ProductGroupSuggestionDto,
} from '../models/anymarket-publication.model';

@Injectable({ providedIn: 'root' })
export class AnyMarketPublicationService extends BaseHttpClient {
  private readonly api = `${environment.gateway.url}/backoffice/v2/anymarket/extractions`;
  private readonly brandsApi = `${environment.gateway.url}/backoffice/v2/brands`;
  private readonly productGroupsApi = `${environment.gateway.url}/backoffice/v2/product-groups`;

  create(filters: AnyMarketExtractionFilter) {
    return this.http.post<AnyMarketExtractionDto>(this.api, filters);
  }

  list(queryOrPage: PageableQuery | number = {}, size?: number) {
    let query: PageableQuery;
    if (typeof queryOrPage === 'number') {
      query = { page: queryOrPage, size: size ?? 12, sort: 'audit.createdAt,desc' };
    } else {
      query = {
        sort: 'audit.createdAt,desc',
        page: 1,
        size: 12,
        ...queryOrPage,
      };
    }
    return this.http.get<Pageable<AnyMarketExtractionDto>>(this.api, {
      params: this.prepareQuery(query),
    });
  }

  getById(id: string) {
    return this.http.get<AnyMarketExtractionDetailsDto>(`${this.api}/${id}`);
  }

  getDownloadUrls(id: string, files: string[]) {
    return this.http.get<string[]>(`${this.api}/${id}/download`, {
      params: this.prepareQuery({ files: files.join(',') }),
    });
  }

  cancel(id: string) {
    return this.http.delete<void>(`${this.api}/${id}/cancel`);
  }

  suggestBrands(search: string): Observable<AutocompleteItem[]> {
    return this.http
      .get<BrandSuggestionDto[]>(`${this.brandsApi}/suggestions`, {
        params: this.prepareQuery({ search }),
      })
      .pipe(map((items) => items.map(({ id, name }) => ({ id, label: name }))));
  }

  suggestProductGroups(search: string): Observable<AutocompleteItem[]> {
    return this.http
      .get<ProductGroupSuggestionDto[]>(`${this.productGroupsApi}/suggestions`, {
        params: this.prepareQuery({ search }),
      })
      .pipe(map((items) => items.map(({ id, name }) => ({ id, label: name }))));
  }
}
